import { useCallback, useEffect, useRef, useState } from 'react';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import type {
  ActiveRepairProgressContext,
  MappingRepairProgressStepId,
  ScopedRepairStepProgress,
} from '../lib/mappingRepairProgress';
import type { MappingRepairError } from '../lib/mappingRepairExecution';
import { toTraceKey, traceMappingFlow } from '../lib/mappingTrace';
import type { MappingRepairVerificationResult } from '../lib/mappingRepairVerification';
import type { MappingRepairActionResult } from '../lib/mappingRepairRun';

const claimedRepairSignatures = new Set<string>();
const repairInFlightSignatures = new Set<string>();
const repairInFlightListeners = new Map<string, Set<(isRepairing: boolean) => void>>();

function publishRepairInFlightChange(signature: string, isRepairing: boolean): void {
  repairInFlightListeners.get(signature)?.forEach((listener) => listener(isRepairing));
}

export function useMappingRepairLifecycle(input: {
  signature: string;
  attemptScope: string;
  missingParameterNames?: string[];
  isAdding: boolean;
  parameterProgress: OperationProgressModel | null;
  verifyRepairParameters: (
    repairActionResults?: MappingRepairActionResult[]
  ) => Promise<MappingRepairVerificationResult>;
  onRepairInFlightChange?: (isRepairing: boolean) => void;
}) {
  const attemptedSignaturesRef = useRef(new Set<string>());
  const loggedGateKeysRef = useRef(new Set<string>());
  const [repairingSignature, setRepairingSignature] = useState<string | null>(null);
  const [activeRepairProgressContext, setActiveRepairProgressContext] =
    useState<ActiveRepairProgressContext | null>(null);
  const [repairStepProgress, setRepairStepProgress] =
    useState<ScopedRepairStepProgress | null>(null);
  const [activeParameterProgressStepId, setActiveParameterProgressStepId] =
    useState<MappingRepairProgressStepId | null>(null);
  const [repairError, setRepairError] =
    useState<MappingRepairError | null>(null);
  const currentSignatureRef = useRef(input.signature);
  const currentAttemptScopeRef = useRef(input.attemptScope);
  const currentVerifyRepairParametersRef = useRef(input.verifyRepairParameters);
  const latestParameterProgressRef = useRef<OperationProgressModel | null>(null);
  const claimedNotificationSignatureRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  currentSignatureRef.current = input.signature;
  currentAttemptScopeRef.current = input.attemptScope;
  currentVerifyRepairParametersRef.current = input.verifyRepairParameters;
  if (input.isAdding && input.parameterProgress) {
    latestParameterProgressRef.current = input.parameterProgress;
  }

  const missingSetKey = [...(input.missingParameterNames ?? [])]
    .map((name) => name.trim().toLowerCase())
    .sort()
    .join('|');
  useEffect(() => {
    if (missingSetKey !== '' ||
      claimedRepairSignatures.has(input.attemptScope) ||
      (repairError?.signature === input.signature &&
        repairError.retainWhenReadinessSatisfied === true)) {
      return;
    }

    setRepairError((current) => current?.signature === input.signature ? null : current);
    setActiveRepairProgressContext((current) =>
      current?.signature === input.signature && current.failedStepId ? null : current
    );
    setRepairStepProgress((current) => current?.model.status === 'error' ? null : current);
    setActiveParameterProgressStepId(null);
    latestParameterProgressRef.current = null;
  }, [input.attemptScope, input.signature, missingSetKey, repairError, repairingSignature]);

  useEffect(() => {
    const listener = input.onRepairInFlightChange;
    if (!listener) {
      return;
    }
    const listeners = repairInFlightListeners.get(input.signature) ?? new Set();
    listeners.add(listener);
    repairInFlightListeners.set(input.signature, listeners);
    listener(repairInFlightSignatures.has(input.signature));
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        repairInFlightListeners.delete(input.signature);
      }
    };
  }, [input.onRepairInFlightChange, input.signature]);

  const clearRepairAttempt = useCallback(() => {
    claimedRepairSignatures.delete(input.attemptScope);
    attemptedSignaturesRef.current.delete(input.attemptScope);
    setRepairError((current) => current?.signature === input.signature ? null : current);
    setRepairingSignature((current) => current === input.signature ? null : current);
    setActiveRepairProgressContext((current) =>
      current?.signature === input.signature ? null : current
    );
    setRepairStepProgress(null);
    setActiveParameterProgressStepId(null);
    latestParameterProgressRef.current = null;
  }, [input.attemptScope, input.signature]);

  useEffect(() => {
    const attemptedSignatures = attemptedSignaturesRef.current;
    mountedRef.current = true;
    traceMappingFlow('mapping readiness repair lifecycle active', {
      signatureKey: toTraceKey(currentSignatureRef.current),
    });
    return () => {
      mountedRef.current = false;
      attemptedSignatures.forEach((signature) => claimedRepairSignatures.delete(signature));
      if (claimedNotificationSignatureRef.current) {
        repairInFlightSignatures.delete(claimedNotificationSignatureRef.current);
        publishRepairInFlightChange(claimedNotificationSignatureRef.current, false);
        claimedNotificationSignatureRef.current = null;
      }
      traceMappingFlow('mapping readiness repair lifecycle cleanup', {
        signatureKey: toTraceKey(currentSignatureRef.current),
      });
    };
  }, []);

  const claimRepair = useCallback((): boolean => {
    if (attemptedSignaturesRef.current.has(input.attemptScope) ||
        claimedRepairSignatures.has(input.attemptScope)) {
      return false;
    }
    claimedRepairSignatures.add(input.attemptScope);
    repairInFlightSignatures.add(input.signature);
    claimedNotificationSignatureRef.current = input.signature;
    attemptedSignaturesRef.current.add(input.attemptScope);
    publishRepairInFlightChange(input.signature, true);
    return true;
  }, [input.attemptScope, input.signature]);
  const releaseRepair = useCallback(() => {
    claimedRepairSignatures.delete(input.attemptScope);
    repairInFlightSignatures.delete(input.signature);
    claimedNotificationSignatureRef.current = null;
    publishRepairInFlightChange(input.signature, false);
  }, [input.attemptScope, input.signature]);

  return {
    attemptedSignaturesRef,
    loggedGateKeysRef,
    repairingSignature,
    setRepairingSignature,
    activeRepairProgressContext,
    setActiveRepairProgressContext,
    repairStepProgress,
    setRepairStepProgress,
    activeParameterProgressStepId,
    setActiveParameterProgressStepId,
    repairError,
    setRepairError,
    currentSignatureRef,
    currentAttemptScopeRef,
    currentVerifyRepairParametersRef,
    latestParameterProgressRef,
    mountedRef,
    clearRepairAttempt,
    alreadyAttemptedRepair: attemptedSignaturesRef.current.has(input.attemptScope),
    claimRepair,
    releaseRepair,
  };
}
